import {
  CsvExport,
  ExcelExport,
  ExcelSheet,
} from '../models/api/core/export/export';
import { ReportField } from '../models/api/reports/report';

export class ReportFieldExportMapper {
  /**
   * Converts report fields to CSV export format
   * @param fields Array of report fields (leaf nodes only)
   * @param reportName Name of the report
   * @param clientName Client identifier
   * @returns CsvExport model for backend processing
   */
  static toCsvExport(
    fields: ReportField[],
    reportName: string,
    clientName: string,
  ): CsvExport {
    const headers = ['Field Name', 'Field Description'];
    const dataRows = fields.map((field) => [
      field.defaultValue || '',
      field.description || '',
    ]);

    // Add blank row separator
    dataRows.push(['', '']);

    // Add metadata row
    const metadataMessage = `This export includes report fields from the ${reportName} report for ${clientName}.`;
    dataRows.push([metadataMessage, '']);

    return {
      fileName: this.generateFileName(reportName, 'csv'),
      headers,
      rows: dataRows,
    };
  }

  /**
   * Converts report fields to Excel export format with 2 worksheets
   * @param fields Array of report fields (leaf nodes only)
   * @param reportName Name of the report
   * @param clientName Client identifier
   * @returns ExcelExport model for backend processing
   */
  static toExcelExport(
    fields: ReportField[],
    reportName: string,
    clientName: string,
  ): ExcelExport {
    const fieldDataSheet: ExcelSheet = {
      label: 'Field List',
      headers: ['Field Name', 'Field Description'],
      rows: fields.map((field) => [
        field.defaultValue || '',
        field.description || '',
      ]),
    };

    const metadataMessage = `This export includes report fields from the ${reportName} report for ${clientName}.`;
    const metadataSheet: ExcelSheet = {
      label: 'Export Info',
      headers: ['Information'],
      rows: [[metadataMessage]],
    };

    return {
      fileName: this.generateFileName(reportName, 'xlsx'),
      sheets: [fieldDataSheet, metadataSheet],
    };
  }

  /**
   * Generates a standardized filename for export
   * @param reportName Name of the report
   * @param extension File extension (csv or xlsx)
   * @returns Formatted filename
   */
  private static generateFileName(
    reportName: string,
    extension: string,
  ): string {
    // Sanitize report name for filename (remove special characters)
    const sanitized = reportName.replace(/[^a-zA-Z0-9-_]/g, '_');
    const dateStamp = new Date().toISOString().split('T')[0];
    return `ReportFields_${sanitized}_${dateStamp}.${extension}`;
  }
}
